<template>
  <section>
    <div class="form">
      <div class="inner">
        <form ref="signUpForm" @submit="submit">
          <h4>{{visualSettings.signUpHeaderLabel}}</h4>

          <transition name="fade" mode="out-in">
            <div v-if="!showingSignUpCode" key="1">
              <label for="signUpFullName">Full Name</label>
              <input
                ref="signUpFullName"
                type="text"
                pattern="\S(?:\s*\S){0}.*\S"
                title="Name must be at least 2 characters"
                autocomplete="name"
                id="signUpFullName"
                name="name"
                placeholder="Full Name"
                v-model="signUpName"
                :disabled="showingSignUpCode"
                required
              />

              <label for="signUpEmail">Email Address (optional)</label>
              <input
                type="text"
                autocomplete="email"
                pattern="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[a-z]{2,}$"
                title="Email address must be valid"
                id="signUpEmail"
                name="email"
                placeholder="Email Address"
                v-model="signUpEmail"
                :disabled="showingSignUpCode || !enableSignUpEmailField"
              />
            </div>
            <div v-if="showingSignUpCode" key="2">
              <div class="notification" v-if="showingSignUpCode && !showingSignUpIncorrectCode">
                <Notification
                  :message="`A verification code has been sent to <strong>${signUpEmail}</strong>. Please enter it below.`"
                  :icon="['fas', 'paper-plane']"
                  type="info"
                />
              </div>

              <div class="notification" v-if="showingSignUpIncorrectCode">
                <Notification
                  message="Incorrect verification code. Please try again."
                  :icon="['fas', 'exclamation-circle']"
                  type="error"
                />
              </div>

              <label for="signUpCode">Verification Code</label>
              <input
                type="text"
                ref="signUpCode"
                id="signUpCode"
                name="code"
                placeholder="********"
                v-model="code"
                maxlength="20"
                :disabled="!showingSignUpCode"
                required
              />
            </div>
          </transition>

          <button type="submit" :disabled="processing" aria-label="Sign in">
            <span class="spinner" v-if="processing">
              <fa-icon
                icon="spinner"
                size="1x"
                :spin="true"
              />
            </span>
            Sign In
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import Notification from '@/components/Notification';

export default {
  name: 'SignUp',
  components: {
    Notification
  },
  data() {
    return {
      name: '',
      email: '',
      code: '',
      processing: false,
      enableSignUpEmailField: this.signUpEmail === undefined || this.signUpEmail.length === 0
    };
  },
  computed: {
    ...mapGetters([
      'visualSettings'
    ]),
    ...mapState([
      'showingSignUpCode',
      'showingSignUpIncorrectCode',
      'signUpEmail'
    ]),
    signUpName: {
      get() {
        return this.$store.state.signUpName;
      },
      set(value) {
        this.setSignUpName(value);
      }
    },
    signUpEmail: {
      get() {
        return this.$store.state.signUpEmail;
      },
      set(value) {
        this.setSignUpEmail(value);
      }
    }
  },
  methods: {
    ...mapMutations([
      'setSignUpName',
      'setSignUpEmail'
    ]),
    ...mapActions([
      'signUpUser'
    ]),
    async submit(e) {
      e.preventDefault();
      if(!this.$refs.signUpForm.checkValidity() || this.processing) {
        return;
      }

      this.processing = true;
      await this.signUpUser({
        name: this.signUpName,
        email: this.signUpEmail.length ? this.signUpEmail : undefined,
        code: this.code.length ? this.code : undefined
      });
      this.processing = false;
    }
  },
  mounted() {
    this.enableSignUpEmailField = this.signUpEmail === undefined || this.signUpEmail.length === 0;
    this.$refs.signUpFullName && this.$refs.signUpFullName.focus();
    this.$refs.signUpCode && this.$refs.signUpCode.focus();
  }
}
</script>

<style scoped lang="scss">
section {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: rgba(255,255,255,1);
}

.form {
  display: table;
  width: 300px;
  height: 90%;
  margin: 0 auto;

  > .inner {
    display: table-cell;
    vertical-align: middle;
  }
}

form {
  background: #fff;
  padding: 16px;
  border-radius: 5px;
  box-shadow: 0 4px 16px rgba(60, 40, 40, 0.12);
}

.notification {
  margin: 12px 0 0 0;
}

h4 {
  margin: 0;
  font-size: 20px;
  line-height: 1;
}

label {
  display: block;
  color: rgba(47, 49, 56, 0.35);
  font-size: 12px;
  margin: 12px 0 2px;
}

input {
  display: block;
  background: #fff;
  border: 1px solid #c8cbd2;
  font-size: 16px;
  font-family: "Poppins";
  box-shadow: 0px 6px 12px 0px rgba(95, 103, 109, 0.09);
  border-radius: 4px;
  display: block;
  width: 100%;
  padding: 12px 15px;
  height: 40px;
  box-sizing: border-box;
  transition: box-shadow .25s ease-out,
    border-color .2s;

  @media only screen and (max-width: 768px) {
    padding-left: 10px;
    padding-right: 10px;
  }

  &::placeholder,
  &::-webkit-input-placeholder,
  :-ms-input-placeholder {
    color: rgb(47,49,56);
  }

  &:focus {
    outline: none;
    border: 1px solid #929aad;
  }
}

button {
  margin: 14px 0 0 0;
  width: 100%;
  font-size: 16px;
  font-family: "Poppins";
  text-align: center;
  height: 40px;
  padding: 0 15px;
  color: #fff;
  box-sizing: border-box;
  border-radius: 4px;
  border: 0;
  background: #2bbd7e;
  cursor: pointer;

  .spinner {
    position: relative;
    left: -20px;
    width: 20px;
    margin: 0 -20px 0 0;
  }

  &:disabled {
    cursor: default;
  }
}

.sign-up-enter-active, .sign-up-leave-active {
  transition: all 750ms cubic-bezier(0.230, 1.000, 0.320, 1.000);

  form {
    transition: all 750ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
  }
}

.sign-up-leave-to {
  opacity: 0;

  form {
    transform: scale(0.9);
  }
}

.sign-up-enter {
  form {
    transform: scale(0.9);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all 250ms cubic-bezier(0.230, 1.000, 0.320, 1.000);
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
